import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatCard, Panel, Pill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import {
  MagnifyingGlass, PhoneCall, Calendar, TrendUp, Sparkle, UserPlus,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATUS_TONES = {
  new: "warning",
  contacted: "info",
  visited: "info",
  warm: "warning",
  hot: "danger",
  converted: "success",
  lost: "muted",
};

export default function CounsellorDashboard() {
  const { user } = useAuth();
  const [allEnquiries, setAllEnquiries] = useState([]);
  const [myEnquiries, setMyEnquiries] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [tab, setTab] = useState("mine"); // mine | unassigned | all
  const [autoAssigning, setAutoAssigning] = useState(false);

  async function load() {
    try {
      const all = await api.get("/enquiries");
      setAllEnquiries(all.data || []);
      const mine = user?.role === "counsellor"
        ? await api.get("/enquiries/mine").then((r) => r.data.items)
        : [];
      setMyEnquiries(mine);
      const unass = (all.data || []).filter(
        (e) => !e.counsellor_id && e.status !== "converted" && e.status !== "lost",
      );
      setUnassigned(unass);
      const cs = await api.get("/users", { params: { role: "counsellor" } });
      setCounsellors(cs.data.items || cs.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load");
    }
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const isCounsellor = user?.role === "counsellor";
  const canManage = !isCounsellor;

  const visible = isCounsellor
    ? (tab === "mine" ? myEnquiries : tab === "unassigned" ? unassigned : allEnquiries)
    : (tab === "unassigned" ? unassigned : allEnquiries);

  const counts = (isCounsellor ? myEnquiries : allEnquiries).reduce((m, e) => {
    m[e.status] = (m[e.status] || 0) + 1;
    return m;
  }, {});

  async function claim(enq) {
    try {
      await api.post(`/enquiries/${enq.enquiry_id}/assign`, {
        counsellor_id: user.user_id,
        note: "Self-claimed",
      });
      toast.success("Enquiry claimed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not claim");
    }
  }

  async function assign(enq, counsellorId) {
    try {
      await api.post(`/enquiries/${enq.enquiry_id}/assign`, {
        counsellor_id: counsellorId,
      });
      toast.success("Assigned");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not assign");
    }
  }

  async function autoAssign() {
    setAutoAssigning(true);
    try {
      const r = await api.post("/enquiries/auto-assign", null, {
        params: user?.branch_id ? { branch_id: user.branch_id } : {},
      });
      toast.success(`Auto-assigned ${r.data.assigned} enquiries across ${r.data.counsellors} counsellor(s)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not auto-assign");
    } finally {
      setAutoAssigning(false);
    }
  }

  return (
    <div data-testid="counsellor-dashboard">
      <PageHeader
        eyebrow={isCounsellor ? "Counsellor" : "Counsellor management"}
        title={isCounsellor ? "Your admissions pipeline" : "Counsellor pipeline & assignment"}
        subtitle="Warm leads, clear next steps, no one slipping through."
        action={
          canManage && (
            <Button
              onClick={autoAssign}
              disabled={autoAssigning || unassigned.length === 0}
              className="bg-terracotta hover:bg-terracotta/90 text-white"
              data-testid="auto-assign-btn"
            >
              <Sparkle size={16} /> {autoAssigning ? "Assigning…" : `Auto-assign (${unassigned.length})`}
            </Button>
          )
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard label="My enquiries" value={isCounsellor ? myEnquiries.length : allEnquiries.length} icon={MagnifyingGlass} accent="lavender" />
        <StatCard label="New" value={counts.new || 0} icon={PhoneCall} accent="terracotta" />
        <StatCard label="Contacted" value={counts.contacted || 0} icon={Calendar} accent="mustard" />
        <StatCard label="Visited" value={counts.visited || 0} icon={Calendar} accent="sage" />
        <StatCard label="Converted" value={counts.converted || 0} icon={TrendUp} accent="sage" />
      </div>

      <div className="flex gap-2 mb-4" data-testid="counsellor-tabs">
        {isCounsellor && (
          <button onClick={() => setTab("mine")} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${tab === "mine" ? "bg-terracotta text-white border-terracotta" : "bg-paper text-ink-soft border-border"}`} data-testid="tab-mine">My enquiries ({myEnquiries.length})</button>
        )}
        <button onClick={() => setTab("unassigned")} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${tab === "unassigned" ? "bg-terracotta text-white border-terracotta" : "bg-paper text-ink-soft border-border"}`} data-testid="tab-unassigned">Unassigned ({unassigned.length})</button>
        <button onClick={() => setTab("all")} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${tab === "all" ? "bg-terracotta text-white border-terracotta" : "bg-paper text-ink-soft border-border"}`} data-testid="tab-all">All ({allEnquiries.length})</button>
      </div>

      <Panel title={
        tab === "mine" ? "Your active enquiries" :
        tab === "unassigned" ? "Unassigned enquiries" : "All enquiries"
      } right={<Link to="/app/enquiries" className="text-sm text-terracotta font-semibold">Open pipeline →</Link>}>
        <div className="space-y-2">
          {visible.length === 0 && (
            <div className="text-ink-soft text-sm py-12 text-center">Nothing here yet.</div>
          )}
          {visible.slice(0, 30).map((e) => (
            <div
              key={e.enquiry_id}
              className="flex items-center justify-between p-3 rounded-2xl border border-border"
              data-testid={`enq-row-${e.enquiry_id}`}
            >
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">
                  {e.parent_name}{" "}
                  {e.relationship && <span className="text-ink-soft text-xs">({e.relationship}{e.guardian_type ? `, ${e.guardian_type}` : ""})</span>}
                  <span className="text-ink-soft"> · {e.child_name}</span>
                </div>
                <div className="text-xs text-ink-soft truncate">
                  {e.phone} · Source: {e.source}
                  {e.counsellor_name ? ` · Assigned: ${e.counsellor_name}` : " · Unassigned"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Pill tone={STATUS_TONES[e.status] || "muted"}>{e.status?.replace("_", " ")}</Pill>
                {tab === "unassigned" && isCounsellor && (
                  <Button
                    size="sm"
                    onClick={() => claim(e)}
                    className="bg-terracotta hover:bg-terracotta/90 text-white"
                    data-testid={`claim-${e.enquiry_id}`}
                  >
                    <UserPlus size={14} /> Claim
                  </Button>
                )}
                {tab === "unassigned" && canManage && counsellors.length > 0 && (
                  <select
                    onChange={(ev) => ev.target.value && assign(e, ev.target.value)}
                    defaultValue=""
                    className="text-xs p-1 rounded border border-border bg-paper"
                    data-testid={`assign-${e.enquiry_id}`}
                  >
                    <option value="">Assign…</option>
                    {counsellors.map((c) => (
                      <option key={c.user_id} value={c.user_id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
